import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Upload, Bot, CheckCircle, FileOutput, TrendingUp, Clock, Users, Lock, Zap, Star, Award, Check, Activity } from "lucide-react";
import { RestoreLogo } from "@/components/RestoreLogo";
import { STATUS_PAGE_URL } from "@/lib/statusPage";
import { useRef, useEffect } from "react";
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";

// ─── Animated stat card ────────────────────────────────────────────────────────

interface StatProps {
  prefix?: string;
  value: number;
  suffix?: string;
  label: string;
  sublabel: string;
  icon: React.ElementType;
  delay?: number;
}

function AnimatedStat({ prefix = "", value, suffix = "", label, sublabel, icon: Icon, delay = 0 }: StatProps) {
  const ref = useRef<HTMLDivElement>(null);
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v) => Math.round(v));
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  useEffect(() => {
    if (isInView) {
      animate(motionValue, value, { duration: 2.2, ease: [0.22, 1, 0.36, 1], delay });
    }
  }, [isInView, value, motionValue, delay]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6, transition: { type: "spring", stiffness: 400, damping: 20 } }}
      className="relative text-center"
      style={{
        background: "linear-gradient(135deg, rgba(52,188,113,0.08) 0%, rgba(0,0,0,0) 65%)",
        border: "1px solid rgba(52,188,113,0.25)",
        borderRadius: 20,
        padding: "2rem 1.5rem",
        overflow: "hidden",
      }}
    >
      {/* Radial glow behind the number */}
      <div
        style={{
          position: "absolute",
          top: -30,
          left: "50%",
          transform: "translateX(-50%)",
          width: 160,
          height: 160,
          background: "radial-gradient(circle, rgba(52,188,113,0.18) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Icon */}
      <div
        className="mx-auto mb-5 flex items-center justify-center"
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: "rgba(52,188,113,0.12)",
          border: "1px solid rgba(52,188,113,0.3)",
          position: "relative",
        }}
      >
        <Icon className="w-7 h-7" style={{ color: "hsl(142,70%,52%)" }} />
      </div>

      {/* Animated number */}
      <div
        className="mb-2 tabular-nums"
        style={{
          fontSize: "3.5rem",
          fontWeight: 800,
          letterSpacing: "-0.04em",
          background: "linear-gradient(135deg, hsl(142,70%,68%) 0%, hsl(142,65%,45%) 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          lineHeight: 1.1,
        }}
      >
        {prefix}
        <motion.span>{rounded}</motion.span>
        {suffix}
      </div>

      <h4 className="text-lg font-bold text-white mb-1.5">{label}</h4>
      <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>
        {sublabel}
      </p>
    </motion.div>
  );
}

// ─── Landing Page ──────────────────────────────────────────────────────────────

const workflowSteps = [
  {
    icon: Upload,
    title: "Upload Photos & Notes",
    body: "Utilize existing field tools for data capture. Zero learning curve.",
  },
  {
    icon: Bot,
    title: "AI Builds Draft Scope",
    body: "AI generates a comprehensive draft estimate with S500 checks built-in.",
  },
  {
    icon: CheckCircle,
    title: "Review & Approve",
    body: "Estimator reviews and edits the AI-generated scope in minutes, not hours.",
  },
  {
    icon: FileOutput,
    title: "Export",
    body: "Finalized, clean file exports directly to your estimating platform.",
  },
];

const typicalResults = [
  {
    icon: TrendingUp,
    prefix: "+",
    value: 30,
    suffix: "%",
    label: "First-Pass Acceptance",
    sublabel: "Immediate scope acceptance by reviewers.",
    delay: 0,
  },
  {
    icon: Clock,
    prefix: "-",
    value: 7,
    suffix: "",
    label: "Days Saved",
    sublabel: "Time to final claim approval.",
    delay: 0.12,
  },
  {
    icon: Users,
    prefix: "-",
    value: 8,
    suffix: "",
    label: "Hours Saved",
    sublabel: "Per estimate on rework.",
    delay: 0.24,
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--restore-dark-bg)" }}>
      {/* ── Nav ── */}
      <header
        className="border-b sticky top-0 z-50"
        style={{
          borderColor: "hsl(220, 12%, 14%)",
          backgroundColor: "hsla(220, 20%, 9%, 0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <RestoreLogo size="md" showTagline />
            <div className="flex items-center gap-3">
              <Link href="/login">
                <Button
                  variant="ghost"
                  className="text-white hover:text-gray-900 hover:bg-white font-semibold"
                  data-testid="button-signin"
                >
                  Sign In
                </Button>
              </Link>
              <Link href="/signup">
                <Button
                  className="text-gray-900 font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "var(--restore-accent)" }}
                  data-testid="button-signup"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="py-24 px-6">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight"
            >
              The RestoreAI Solution:
              <br />
              <span style={{ color: "var(--restore-accent)" }}>Accelerated Accuracy</span>
            </motion.h2>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="text-2xl md:text-3xl font-semibold text-white mb-8"
            >
              HOW RESTOREAI WORKS:
              <br />
              <span style={{ color: "var(--restore-text-muted)" }}>
                Seamless Integration and Precision
              </span>
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="text-xl mb-10"
              style={{ color: "var(--restore-text-muted)" }}
            >
              RestoreAI turns field photos and notes into carrier-ready scopes &amp; estimates—cutting
              claim cycles from weeks to days.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link href="/signup">
                <Button
                  size="lg"
                  className="text-gray-900 font-semibold text-lg px-10 py-6 rounded-lg hover:opacity-90"
                  style={{ backgroundColor: "var(--restore-accent)" }}
                >
                  Start Free Trial
                </Button>
              </Link>
              <Link href="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent text-white font-semibold text-lg px-10 py-6 rounded-lg border-2 hover:bg-white hover:text-gray-900 transition-all"
                  style={{ borderColor: "var(--restore-accent)" }}
                >
                  Sign In
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Workflow Steps ── */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflowSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  className="relative h-full"
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ scale: 1.04, transition: { type: "spring", stiffness: 400, damping: 20 } }}
                >
                  <div
                    className="p-8 rounded-xl border-2 h-full flex flex-col"
                    style={{
                      backgroundColor: "rgba(128,255,0,0.06)",
                      borderColor: "var(--restore-accent)",
                      minHeight: 280,
                    }}
                  >
                    <div className="flex justify-center mb-4">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "var(--restore-accent)" }}
                      >
                        <Icon className="w-8 h-8 text-gray-900" />
                      </div>
                    </div>
                    <h4 className="text-xl font-bold text-white text-center mb-3">{step.title}</h4>
                    <p className="text-sm text-center" style={{ color: "var(--restore-text-muted)" }}>
                      {step.body}
                    </p>
                  </div>
                  {i < workflowSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <div
                        className="w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent"
                        style={{ borderLeftColor: "var(--restore-accent)" }}
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── What You Get ── */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="text-3xl font-bold text-white text-center mb-4"
          >
            WHAT YOU GET: Consistency and Compliance at Scale
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              [
                "No software changes — works with existing tools.",
                "Draft estimates ready in seconds.",
                "Built-in S500 and carrier-specific compliance checks.",
              ],
              [
                "Evidence gap alerts before submission.",
                "Clean, immutable audit trail for carrier/TPA.",
                "Consistent quality across your entire operation.",
              ],
            ].map((items, col) => (
              <motion.div
                key={col}
                initial={{ opacity: 0, x: col === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: col * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="p-6 rounded-lg border"
                style={{ borderColor: "hsl(220, 12%, 22%)" }}
              >
                <ul className="space-y-3">
                  {items.map((item) => (
                    <li key={item} className="flex items-start">
                      <span className="mr-3 mt-0.5" style={{ color: "var(--restore-accent)" }}>•</span>
                      <span className="text-white">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Typical Results ── */}
      <section
        className="py-20 px-6"
        style={{ backgroundColor: "rgba(52,188,113,0.04)" }}
      >
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="border-l-4 pl-6 mb-14"
            style={{ borderColor: "var(--restore-accent)" }}
          >
            <h3 className="text-3xl font-bold text-white mb-2">
              TYPICAL RESULTS: Data-Driven Performance
            </h3>
            <p style={{ color: "var(--restore-text-muted)" }}>
              Based on 30–45 day pilot programs with national restoration firms.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {typicalResults.map((stat) => (
              <AnimatedStat key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Security ── */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-xl border p-8"
            style={{ borderColor: "hsl(220, 12%, 22%)" }}
          >
            <div className="flex items-center justify-center mb-6">
              <Lock className="w-8 h-8 mr-3" style={{ color: "var(--restore-accent)" }} />
              <h3 className="text-2xl font-bold text-white">
                ENTERPRISE-READY SECURITY &amp; INTEGRATION
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">Security &amp; Compliance</h4>
                <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>
                  TLS in transit • AES-256 at rest • OAuth 2.0 • RBAC • Audit Logs • SOC 2 Roadmap
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">Standards-Aware Prompts</h4>
                <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>
                  IICRC S500, EPA Mold Guidance, OSHA PPE
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="py-20 px-6" style={{ backgroundColor: "rgba(128,255,0,0.03)" }}>
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-12"
          >
            <h3 className="text-4xl font-bold text-white mb-4">Simple, Transparent Pricing</h3>
            <p className="text-xl" style={{ color: "var(--restore-text-muted)" }}>
              Choose the plan that fits your workflow. No hidden fees.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                tier: "technician",
                name: "Solo Basic",
                price: "$39",
                icon: Zap,
                gradient: "from-blue-500 to-blue-600",
                features: ["Up to 5 projects per month", "AI-powered features", "Basic field tools"],
                popular: false,
              },
              {
                tier: "solo",
                name: "Solo Pro",
                price: "$59",
                icon: Star,
                gradient: "from-emerald-500 to-emerald-600",
                features: ["Up to 10 projects per month", "AI-powered features", "Basic field tools"],
                popular: true,
              },
              {
                tier: "organization",
                name: "Solo Expert",
                price: "$79",
                icon: Award,
                gradient: "from-purple-500 to-purple-600",
                features: ["Up to 25 projects per month", "AI-powered features", "Basic field tools"],
                popular: false,
              },
            ].map((plan, i) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, transition: { type: "spring", stiffness: 400, damping: 20 } }}
                  className={`relative rounded-2xl border p-8 flex flex-col ${
                    plan.popular
                      ? "border-[var(--restore-accent)] ring-2 ring-[var(--restore-accent)]"
                      : "border-gray-700"
                  }`}
                  style={{
                    backgroundColor: plan.popular
                      ? "rgba(52,188,113,0.06)"
                      : "rgba(255,255,255,0.03)",
                  }}
                >
                  {plan.popular && (
                    <div
                      className="absolute top-0 right-0 rounded-bl-lg rounded-tr-2xl px-3 py-1 text-xs font-bold text-gray-900"
                      style={{ backgroundColor: "var(--restore-accent)" }}
                    >
                      POPULAR
                    </div>
                  )}
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center mb-4`}
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1">{plan.name}</h4>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-sm" style={{ color: "var(--restore-text-muted)" }}>
                      /month
                    </span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm"
                        style={{ color: "var(--restore-text-muted)" }}
                      >
                        <Check
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: "var(--restore-accent)" }}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link href={`/signup?plan=${plan.tier}`}>
                    <Button
                      className={`w-full font-semibold bg-gradient-to-r ${plan.gradient} text-white hover:opacity-90`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h3 className="text-4xl font-bold text-white mb-6">Get Families Home Faster</h3>
            <p className="text-xl mb-10" style={{ color: "var(--restore-text-muted)" }}>
              Join the pilot program and experience how RestoreAI transforms your restoration
              workflow with AI-powered precision and carrier-ready outputs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/signup">
                <Button
                  size="lg"
                  className="text-gray-900 font-semibold text-lg px-10 py-6 rounded-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "var(--restore-accent)" }}
                  data-testid="button-getstarted"
                >
                  Start Free Trial
                </Button>
              </Link>
              <Link href="/signup">
                <Button
                  size="lg"
                  className="bg-transparent text-white border-2 text-lg px-10 py-6 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all"
                  style={{ borderColor: "var(--restore-accent)", color: "white" }}
                  data-testid="button-joinpilot"
                >
                  Join the Pilot
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-800 py-8 px-6">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <RestoreLogo size="sm" />
          <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>
            © 2025 RestoreAI. Inspector, TPA, and Carrier Ready.
          </p>
          <a
            href={STATUS_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm hover:text-white transition-colors"
            style={{ color: "var(--restore-text-muted)" }}
          >
            <Activity className="w-4 h-4" />
            System Status
          </a>
        </div>
      </footer>
    </div>
  );
}
